import { IconChevronDown, IconChevronUp, IconSelector } from "@tabler/icons-react";
import { Fragment, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { DesktopOnly } from "@/components/ui";
import { cn } from "@/lib/cn";
import { Card, DatePickerInput, Group, Input, Loader, Select, Table, Tbody, Td, Text, Th, Thead, Tr } from "@/components/ui";
import { formatIso, parseIso } from "@/features/datos-generales/utils/iso-date";
import type { MiSolicitud } from "@/lib/types";
import {
  filtrarSolicitudes,
  ordenarSolicitudes,
  siguienteSort,
  type SolicitudFiltros,
  type SolicitudSort,
  type SolicitudSortKey,
} from "../utils/ordenar-solicitudes";
import { tipoDetalleDe } from "../utils/detalle-expandible";
import { placeholderEjemplo, type FacturaFiltroKey, type FiltrosFactura } from "../utils/filtros-factura";
import { FilaFiltrosFactura } from "./FilaFiltrosFactura";
import { OPCIONES_FILTRO_ESTADO } from "../utils/filtro-estado";

/** Valor de la opción «Todos» del filtro de estado (Radix Select no admite valor vacío). */
const TODOS_ESTADOS = "__todos";
import { FacturasDeSolicitudRow } from "./FacturasDeSolicitudRow";
import { InvoiceDeSolicitudRow } from "./InvoiceDeSolicitudPanel";
import { SolicitudRow } from "./SolicitudRow";

/** Ancho mínimo de la tabla · por debajo scrollea horizontalmente (compartido
 *  por la tabla y su barra de scroll espejo para que coincidan al pixel). */
export const MIN_WIDTH_TABLA = 1024;

/**
 * Columnas consolidadas (revisión UX): Proveedor absorbe el RFC (2ª línea),
 * Total absorbe la Moneda (código ISO inline · regla de `AGENTS.md` para
 * vistas multi-moneda) y Núm. Doc se retira (HOTSPOT-DOMINIO sin fuente de
 * dato · era una columna permanentemente vacía). El espacio liberado va a
 * Descripción, la columna que el usuario lee para reconocer una solicitud.
 */
export function columnasSolicitudes(): {
  key: SolicitudSortKey | null;
  label: string;
  align?: "right" | "center";
  w: number;
  filtro?: "fecha" | "proveedor" | "estado";
  /** Placeholder del filtro: un valor REAL y COMPLETO de ejemplo, no el nombre de la columna. */
  ejemplo?: string;
  /** Líneas de la celda (dato + subdato) · solo lo usa el skeleton de carga. */
  lineas?: 2;
}[] {
  return [
    // Columna ESTRECHA del chevron de la fila expandible (facturas de la solicitud ·
    // patrón "expandable row" de IBM Carbon). Sin etiqueta visible (sr-only).
    { key: null, label: "Expandir", w: 34 },
    // Pesos RELATIVOS (se normalizan a %) · el folio son 15 dígitos (~148px) · un
    // token que NO parte, así que conserva la mayor proporción para no cortarse.
    // La columna Moneda se fusionó en Total (código ISO como 2ª línea) · una
    // columna menos para que la tabla quepa SIN scroll horizontal.
    { key: "folio", label: "Folio", w: 170, ejemplo: "520000000100153" },
    // Estado con más peso: es el dato más escaneado y no debe truncarse a
    // ambigüedad ("AUTORIZA…"). El texto además hace wrap (no trunca) en la fila.
    { key: "estado", label: "Estado", w: 145, filtro: "estado" },
    { key: "tipo", label: "Tipo", w: 104, ejemplo: "FB60" },
    // Clave del contrato anualizado (CA) o de la orden de compra (OC) · «—» en FB60.
    { key: "caOc", label: "CA/OC", w: 137, ejemplo: "4500078044" },
    { key: "organizacion", label: "Organización", w: 138, ejemplo: "Banco Ejemplo" },
    { key: "area", label: "Área", w: 97, ejemplo: "Legal" },
    { key: "proveedor", label: "Proveedor", w: 170, filtro: "proveedor", lineas: 2, ejemplo: "GPS120130S8A" },
    { key: "descripcion", label: "Descripción", w: 142, ejemplo: "REMANENTES" },
    { key: "total", label: "Total", align: "right", w: 130, lineas: 2, ejemplo: "2449.92" },
    // Anchos con margen para que el placeholder del filtro de fecha
    // ("Fecha pago…" / "Creación…" + ícono de calendario) quepa en una línea.
    { key: "fechaPago", label: "Fecha pago", w: 145, filtro: "fecha", ejemplo: "16/08/2026" },
    { key: "creacion", label: "Creación", w: 145, filtro: "fecha", ejemplo: "09/09/2026" },
    { key: null, label: "", align: "right", w: 110 },
  ];
}

export function SolicitudesTable({
  solicitudes,
  filtros,
  sort,
  buscando = false,
  mostrarFiltros = true,
  expandirTodas = false,
  filtrosFactura = {},
  onFiltrarFactura,
  onFiltrar,
  onOrdenar,
}: {
  solicitudes: MiSolicitud[];
  filtros: SolicitudFiltros;
  sort: SolicitudSort | null;
  buscando?: boolean;
  /** Fila de filtros por columna colapsable (revisión UX) · el toggle vive en la toolbar de la página. */
  mostrarFiltros?: boolean;
  /**
   * Switch «Mostrar facturas» de la página · expande (true) o contrae (false) a la vez todas
   * las filas visibles que tienen facturas o invoice. Las filas se pueden seguir abriendo o
   * cerrando una por una; al cambiar de página/filtro con el switch encendido, las filas
   * nuevas llegan expandidas.
   */
  expandirTodas?: boolean;
  /** Fila de filtros por FACTURA (debajo de la de la solicitud · se muestra con `mostrarFiltros`). */
  filtrosFactura?: FiltrosFactura;
  onFiltrarFactura?: (key: FacturaFiltroKey, valor: string) => void;
  onFiltrar: (key: SolicitudSortKey, valor: string) => void;
  onOrdenar: (sort: SolicitudSort) => void;
}) {
  const COLS = useMemo(() => {
    const cols = columnasSolicitudes();
    const totalPeso = cols.reduce((acc, c) => acc + c.w, 0);
    return cols.map((c) => ({ ...c, pct: `${((c.w / totalPeso) * 100).toFixed(2)}%` }));
  }, []);

  const visibles = useMemo(() => ordenarSolicitudes(filtrarSolicitudes(solicitudes, filtros), sort), [solicitudes, filtros, sort]);

  const alOrdenar = (key: SolicitudSortKey) => onOrdenar(siguienteSort(sort, key));

  // Filas expandidas (facturas de la solicitud) · varias a la vez, como en Carbon.
  // Las facturas se piden SOLO al expandir (la fila de detalle monta la query).
  const [expandidas, setExpandidas] = useState<ReadonlySet<string>>(() => new Set());
  const alternarExpandida = (id: string) =>
    setExpandidas((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Ids de las filas visibles que tienen algo que expandir (mismo criterio que el chevron).
  const idsExpandibles = useMemo(
    () => visibles.filter((s) => tipoDetalleDe(s) !== null).map((s) => s.id),
    [visibles],
  );
  const claveExpandibles = idsExpandibles.join("|");
  useEffect(() => {
    setExpandidas(expandirTodas ? new Set(idsExpandibles) : new Set());
    // `claveExpandibles` resume `idsExpandibles` (evita re-ejecutar por identidad de arreglo).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandirTodas, claveExpandibles]);

  // Scrollbar horizontal DUPLICADO arriba de la tabla (el nativo queda abajo y
  // obliga a bajar para encontrarlo). Una barra superior espejo, sincronizada
  // en ambos sentidos con el contenedor real de la tabla.
  const tablaScrollRef = useRef<HTMLDivElement>(null);
  const barraSuperiorRef = useRef<HTMLDivElement>(null);

  // Sincronía sin flag: fijar scrollLeft al mismo valor no re-dispara scroll,
  // así que el eco converge en un paso (no hay bucle infinito).
  const alScrollBarra = () => {
    if (tablaScrollRef.current && barraSuperiorRef.current)
      tablaScrollRef.current.scrollLeft = barraSuperiorRef.current.scrollLeft;
  };
  const alScrollTabla = () => {
    if (tablaScrollRef.current && barraSuperiorRef.current)
      barraSuperiorRef.current.scrollLeft = tablaScrollRef.current.scrollLeft;
  };

  return (
    <DesktopOnly>
      <Card padding="xs" className="relative p-0">
        {buscando && (
          <Group gap={6} className="pointer-events-none absolute right-3 top-2 z-20">
            <Loader size="xs" color="multiva" />
            <Text size="xs" c="dimmed" fw={500}>
              Buscando…
            </Text>
          </Group>
        )}
        {/* La tabla es `layout: fixed` y ocupa el 100% del contenedor: en
            escritorio (≥1024px de contenido) las columnas se reparten por su
            proporción y el texto TRUNCA con tooltip · NO hay scroll horizontal.
            El `minWidth` bajó a 1024 (antes 1450 · forzaba scroll en pantallas
            de contenido más angosto que eso): sólo por debajo de 1024 la tabla
            scrollea DENTRO de su card para no encimar el folio de 15 dígitos.
            Trade-off aceptado: el header no es sticky (la lista pagina a 15
            filas · poco scroll vertical). */}
        {/* Barra de scroll horizontal ESPEJO arriba de la tabla · su contenido
            interno replica el ancho real de la tabla para que el pulgar mida lo
            mismo; se sincroniza en ambos sentidos con el contenedor de abajo. */}
        <div
          ref={barraSuperiorRef}
          onScroll={alScrollBarra}
          className="scrollbar-suave h-3 shrink-0 overflow-x-auto overflow-y-hidden"
          aria-hidden="true"
        >
          {/* Ancho por CSS EN VIVO (w-full + min-width = max(contenedor, 1024),
              igual que la tabla layout:fixed con minWidth) · sin lag de
              ResizeObserver que hacía aparecer un scrollbar transitorio al animar. */}
          <div className="h-px w-full" style={{ minWidth: MIN_WIDTH_TABLA }} />
        </div>
        {/* Altura acotada · el scroll vertical ocurre DENTRO para que el `thead`
            sticky se pegue; el horizontal (cuando el contenido no cabe) se conduce
            por la barra espejo de arriba (overflow-x hidden = sin barra abajo). */}
        <div
          ref={tablaScrollRef}
          onScroll={alScrollTabla}
          className="scrollbar-suave max-h-[calc(100dvh-18rem)] overflow-x-hidden overflow-y-auto"
        >
        <Table layout="fixed" minWidth={MIN_WIDTH_TABLA} highlightOnHover verticalSpacing="md" horizontalSpacing="sm" className="uppercase">
          {/* Encabezado con fondo gris tenue (`ink-100`) para el contraste del
              diseño de referencia. Ya NO es sticky: dentro del contenedor con
              scroll horizontal, el `position: sticky` lo despegaba y dejaba que
              las filas se colaran por detrás (header desalineado). */}
          <Thead className="sticky top-0 z-10 border-b-0 bg-ink-100 dark:bg-ink-800">
            {/* Encabezado fijo (sticky): las líneas entre filas NO son bordes de tabla
                (con border-collapse no tienen fondo y el contenido se veía pasar por
                ellas al hacer scroll) sino sombras inset en las celdas, y cada fila
                lleva fondo OPACO propio. */}
            <Tr className={cn("border-0 bg-ink-100 dark:bg-ink-800", !mostrarFiltros && "[&>th]:shadow-[inset_0_-1px_0_var(--color-border)]")}>
              {COLS.map((c, i) =>
                c.key ? (
                  <SortableTh key={c.key} sortKey={c.key} sort={sort} onSort={alOrdenar} align={c.align === "right" ? "right" : "left"} w={c.pct}>
                    {c.label}
                  </SortableTh>
                ) : (
                  <Th key={`x-${i}`} align={c.align} style={{ width: c.pct }} className="align-middle">
                    {i === 0 ? (
                      <span className="sr-only">{c.label}</span>
                    ) : (
                      <Text size="xs" fw={600} className="truncate whitespace-nowrap leading-tight" title={c.label}>
                        {c.label}
                      </Text>
                    )}
                  </Th>
                ),
              )}
            </Tr>
            {mostrarFiltros && (
            // Franja diferenciada (opción A elegida por el usuario): tinte
            // tenue RELATIVO al texto (foreground/5) — `muted` en claro es
            // #f8fafc y sobre la card blanca resultaba invisible; foreground/5
            // oscurece sutil en claro y aclara sutil en oscuro. Los inputs
            // (bg-background) ganan cuerpo por contraste. Sin lupa repetida:
            // en una fila entera de filtros, el placeholder ya lo dice todo.
            // Mismo tinte, pero OPACO (mezclado con el fondo del encabezado): el thead es
            // sticky y un fondo translúcido dejaba ver las filas pasar por detrás.
            <Tr className="border-0 bg-[color-mix(in_srgb,var(--color-foreground)_10%,var(--color-ink-100))] dark:bg-[color-mix(in_srgb,var(--color-foreground)_10%,var(--color-ink-800))] [&>th]:shadow-[inset_0_1px_0_var(--color-border)]">
              {COLS.map((c, i) => {
                if (!c.key) return <Th key={`xf-${i}`} className="!px-1 !py-1.5" />;
                const key = c.key;
                return (
                  <Th key={key} className="!px-1 !py-1.5 align-top">
                    {c.filtro === "fecha" ? (
                      <DatePickerInput
                        size="xs"
                        clearable
                        placeholder={c.ejemplo ? placeholderEjemplo(c.ejemplo) : `${c.label}…`}
                        value={parseIso(filtros[key])}
                        onChange={(d) => onFiltrar(key, formatIso(d))}
                        wrapperClassName="normal-case"
                        placeholderClassName="truncate text-[10px] text-muted-foreground/60"
                      />
                    ) : c.filtro === "estado" ? (
                      // Lista desplegable (decisión del usuario): el texto libre no podía
                      // distinguir el nivel del flujo (el estado interno es el mismo del
                      // nivel 2 en adelante). «Todos» limpia el filtro.
                      <Select
                        size="xs"
                        placeholder="Todos"
                        value={filtros.estado || TODOS_ESTADOS}
                        onValueChange={(v) => onFiltrar("estado", v === TODOS_ESTADOS ? "" : v)}
                        options={[{ value: TODOS_ESTADOS, label: "Todos" }, ...OPCIONES_FILTRO_ESTADO]}
                        className="normal-case font-normal"
                        contentClassName="min-w-64 normal-case"
                      />
                    ) : c.filtro === "proveedor" ? (
                      // Input único inteligente (opción A elegida por el
                      // usuario · los dos inputs apilados rompían la armonía
                      // de la fila): si lo tecleado tiene forma de RFC
                      // (3-4 letras seguidas de dígito · un RFC parcial como
                      // "TVI140" ya matchea, y el server busca por contains)
                      // filtra por `rfcProveedor`; cualquier otro texto, por
                      // razón social. Se limpia la llave contraria para no
                      // dejar un AND fantasma al cambiar de forma.
                      <Input
                        size="xs"
                        placeholder={c.ejemplo ? placeholderEjemplo(c.ejemplo) : undefined}
                        value={filtros.rfcProveedor || filtros.proveedor || ""}
                        onChange={(e) => {
                          const v = e.currentTarget.value;
                          const esRfc = /^[A-ZÑ&]{3,4}\d/i.test(v.trim());
                          onFiltrar(esRfc ? "rfcProveedor" : "proveedor", v);
                          onFiltrar(esRfc ? "proveedor" : "rfcProveedor", "");
                        }}
                        aria-label="Filtrar por proveedor (razón social o RFC)"
                        // Placeholder aclarado (60%): el gris pleno pesaba
                        // demasiado con la fila completa de filtros vacíos.
                        className="normal-case placeholder:text-[10px] placeholder:text-muted-foreground/60"
                      />
                    ) : (
                      <Input
                        size="xs"
                        // Placeholder descriptivo por columna ("Folio…",
                        // "Estado…"…) en vez de ocho "Buscar…" idénticos que
                        // no decían qué busca cada uno.
                        placeholder={c.ejemplo ? placeholderEjemplo(c.ejemplo) : `${c.label}…`}
                        value={filtros[key] ?? ""}
                        onChange={(e) => onFiltrar(key, e.currentTarget.value)}
                        aria-label={`Filtrar por ${c.label}`}
                        className="normal-case placeholder:text-[10px] placeholder:text-muted-foreground/60"
                      />
                    )}
                  </Th>
                );
              })}
            </Tr>
            )}
            {mostrarFiltros && onFiltrarFactura && (
              <FilaFiltrosFactura columnas={COLS.map((c) => c.key)} filtros={filtrosFactura} onFiltrar={onFiltrarFactura} />
            )}
          </Thead>
          <Tbody className="transition-opacity duration-120" style={{ opacity: buscando ? 0.5 : 1 }}>
            {visibles.length === 0 ? (
              <Tr>
                <Td colSpan={COLS.length}>
                  <Text size="sm" c="dimmed" ta="center" className="py-3">
                    Sin resultados para los filtros.
                  </Text>
                </Td>
              </Tr>
            ) : (
              visibles.map((s) => {
                const expandida = expandidas.has(s.id);
                const detalleId = `facturas-solicitud-${s.id}`;
                return (
                  <Fragment key={s.id}>
                    <SolicitudRow
                      s={s}
                      expandida={expandida}
                      detalleId={detalleId}
                      onAlternarExpandida={() => alternarExpandida(s.id)}
                    />
                    {expandida &&
                      (tipoDetalleDe(s) === "Invoice" ? (
                        <InvoiceDeSolicitudRow id={detalleId} solicitudId={s.id} folio={s.folio} colSpan={COLS.length} />
                      ) : (
                        <FacturasDeSolicitudRow id={detalleId} solicitudId={s.id} folio={s.folio} colSpan={COLS.length} />
                      ))}
                  </Fragment>
                );
              })
            )}
          </Tbody>
        </Table>
        </div>
      </Card>
    </DesktopOnly>
  );
}

function SortableTh({
  children,
  sortKey,
  sort,
  onSort,
  align = "left",
  w,
}: {
  children: ReactNode;
  sortKey: SolicitudSortKey;
  sort: SolicitudSort | null;
  onSort: (key: SolicitudSortKey) => void;
  align?: "left" | "right";
  w?: string | number;
}) {
  const activa = sort?.key === sortKey;
  const Icono = activa ? (sort!.dir === "asc" ? IconChevronUp : IconChevronDown) : IconSelector;
  return (
    <Th className="!p-0 align-middle" style={{ width: w }}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        aria-label={`Ordenar por ${typeof children === "string" ? children : sortKey}`}
        // Nombre completo al pasar el cursor · el encabezado va en UNA línea y se recorta
        // con «…» si la columna se angosta (p. ej. al expandirse el sidebar), para que la
        // fila de encabezados no cambie de altura.
        title={typeof children === "string" ? children : undefined}
        className="flex w-full min-w-0 items-center justify-start gap-1 px-2.5 py-2"
      >
        <Text size="xs" fw={600} className="min-w-0 truncate whitespace-nowrap uppercase leading-tight text-ink-700 dark:text-ink-200">
          {children}
        </Text>
        <Icono size={15} stroke={2} className={`shrink-0 ${activa ? "text-multiva-600" : "text-ink-600 dark:text-ink-300"}`} />
      </button>
    </Th>
  );
}
